import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Plus, Settings, Trash2, Edit } from 'lucide-react';
import { useUserStore } from '../stores/userStore';
import { supabase } from '../lib/supabase';
import { BackButton } from '../components/common/BackButton';
import type { UserService } from '../types';

export function MyServicesPage() {
  const navigate = useNavigate();
  const { profile } = useUserStore();
  const [services, setServices] = useState<UserService[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/');
        return;
      }

      const { data, error } = await supabase
        .from('user_services')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setServices(data || []);
      setIsLoading(false);
    };

    checkAuth();
  }, [navigate]);

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('user_services')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setServices(services.filter(service => service.id !== id));
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pt-32 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center text-gray-400">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 px-4">
      <div className="max-w-7xl mx-auto">
        <BackButton to="/" label="Back to Home" />
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Package className="w-8 h-8 text-green-500" />
            <h1 className="text-3xl font-bold text-white">My Services</h1>
          </div>
          
          <button
            onClick={() => navigate('/services/new')}
            className="flex items-center gap-2 px-6 py-3 bg-green-500 text-black font-bold rounded-md hover:bg-green-400 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add New Service
          </button>
        </div>

        {services.length === 0 ? (
          <div className="text-center py-12 bg-black/50 border border-green-500/20 rounded-lg">
            <Package className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">No Services Yet</h2>
            <p className="text-gray-400 mb-6">Start by adding your first service</p>
            <button
              onClick={() => navigate('/services/new')}
              className="px-6 py-3 bg-green-500 text-black font-bold rounded-md hover:bg-green-400 transition-colors"
            >
              Add Service
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <div
                key={service.id}
                className="bg-black/50 border border-green-500/20 rounded-lg p-6 hover:border-green-500/40 transition-all"
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold text-white">{service.service_type}</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/services/edit/${service.id}`)}
                      className="p-2 text-gray-400 hover:text-green-500 transition-colors"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(service.id)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-gray-400">
                    <span>Status</span>
                    <span className="capitalize">{service.status}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>Usage</span>
                    <span>{service.current_usage} / {service.usage_limit}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>Next Billing</span>
                    <span>{new Date(service.next_billing_date).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="relative pt-2">
                  <div className="bg-gray-700 h-2 rounded-full">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{
                        width: `${(service.current_usage / service.usage_limit) * 100}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}